import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useUserData } from "../contexts/UserContext";
import { storageService } from "../services/storage.service";
import { syncService } from "../services/sync.service";
import { PersonRecord } from "../types/data";
import { convertInCurrentLang, convertNumberInCurrentLang } from "../utils/misc";

interface OrphanedGroup {
  surveyorId: string;
  persons: PersonRecord[];
  isOrphaned: boolean | null; // null = checking
  count: number;
}

interface DeletedPerson {
  id: string;
  deleted_at: string;
  name?: string;
  surveyor_id?: string;
}

interface DeletedRecordsState {
  locallyDeleted: DeletedPerson[];
  serverDeleted: DeletedPerson[];
}

export default function ManageOrphanedRecords() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user: currentUser, appData, updatePerson } = useUserData();
  const [orphanedGroups, setOrphanedGroups] = useState<OrphanedGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [deletedRecords, setDeletedRecords] = useState<DeletedRecordsState>({
    locallyDeleted: [],
    serverDeleted: [],
  });

  useEffect(() => {
    loadOrphanedPersons();
    loadDeletedPersons();
  }, []);

  const loadDeletedPersons = async () => {
    try {
      const currentUserId = (currentUser as any)?.uuid || currentUser?.id;
      const userData = appData as any;
      
      // Get locally deleted persons (marked for deletion but not yet synced)
      const localDeletedPersons: DeletedPerson[] = Array.isArray(userData?.deleted_persons)
        ? userData.deleted_persons
        : [];
      
      // Filter for current user's deleted records
      const myLocalDeleted = localDeletedPersons.filter((dp) => dp.surveyor_id === currentUserId);
      
      try {
        // Get all deleted persons from server for current user
        const serverDeletedPersons = await syncService.getDeletedPersons();
        
        // Create a set of server-deleted IDs for quick lookup
        const serverDeletedIds = new Set(serverDeletedPersons.map((p) => p.id));
        
        // Split into locally deleted (pending sync) and server deleted (synced)
        const locallyDeleted: DeletedPerson[] = [];
        const serverDeleted: DeletedPerson[] = [];
        
        // Add local deleted records that are NOT on server (pending sync)
        myLocalDeleted.forEach((record) => {
          if (!serverDeletedIds.has(record.id)) {
            locallyDeleted.push(record);
          }
        });
        
        // Add all server-deleted persons
        serverDeletedPersons.forEach((serverPerson) => {
          serverDeleted.push({
            id: serverPerson.id,
            name: serverPerson.name,
            deleted_at: serverPerson.deleted_at,
            surveyor_id: serverPerson.surveyor_id,
          });
        });
        
        setDeletedRecords({ locallyDeleted, serverDeleted });
      } catch (error) {
        console.error('Error fetching deleted persons from server:', error);
        // If server check fails, treat all as locally deleted
        setDeletedRecords({ locallyDeleted: myLocalDeleted, serverDeleted: [] });
      }
    } catch (error) {
      console.error('Error loading deleted persons:', error);
    }
  };

  const loadOrphanedPersons = async () => {
    setLoading(true);
    try {
      const currentUserId = (currentUser as any)?.uuid || currentUser?.id;
      const allPersons = appData?.persons || [];
      
      // Group persons by surveyor_id
      const groupedBySurveyor: { [surveyorId: string]: PersonRecord[] } = {};
      
      allPersons.forEach((person) => {
        const surveyorId = person.surveyor_id || 'unknown';
        
        // Skip current user's own records
        if (surveyorId === currentUserId) return;
        
        if (!groupedBySurveyor[surveyorId]) {
          groupedBySurveyor[surveyorId] = [];
        }
        groupedBySurveyor[surveyorId].push(person);
      });

      // Create groups
      const groups: OrphanedGroup[] = Object.entries(groupedBySurveyor).map(([surveyorId, persons]) => ({
        surveyorId,
        persons,
        isOrphaned: null, // Will check server
        count: persons.length,
      }));

      setOrphanedGroups(groups);
      
      // Check server status for each surveyor
      checkSurveyorsStatus(groups);
    } catch (error) {
      console.error('Error loading orphaned persons:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSurveyorsStatus = async (groups: OrphanedGroup[]) => {
    try {
      // Get all deleted persons from server for current user
      const serverDeletedPersons = await syncService.getDeletedPersons();
      const serverDeletedIds = new Set(serverDeletedPersons.map((p) => p.id));
      
      // Check if each group has any persons deleted on server
      const updatedGroups = groups.map((group) => {
        const hasDeletedPersons = group.persons.some((p) => serverDeletedIds.has(p.id || ''));
        return {
          ...group,
          isOrphaned: hasDeletedPersons,
        };
      });

      setOrphanedGroups(updatedGroups);
    } catch (error) {
      console.error('Error checking server status:', error);
      // If error, mark all as orphaned
      const updatedGroups = groups.map((group) => ({
        ...group,
        isOrphaned: true,
      }));
      setOrphanedGroups(updatedGroups);
    }
  };

  const toggleGroupSelection = (surveyorId: string) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(surveyorId)) {
      newSelected.delete(surveyorId);
    } else {
      newSelected.add(surveyorId);
    }
    setSelectedGroups(newSelected);
  };

  const handleImportSelected = async () => {
    if (selectedGroups.size === 0) return;

    const totalRecords = orphanedGroups
      .filter((g) => selectedGroups.has(g.surveyorId))
      .reduce((sum, g) => sum + g.count, 0);

    const confirmed = window.confirm(
      t('orphanedRecords_confirmImport').replace('{count}', totalRecords.toString())
    );

    if (!confirmed) return;

    setProcessing(true);
    try {
      const currentUserId = (currentUser as any)?.uuid || currentUser?.id;
      const allPersons = appData?.persons || [];
      
      // Update surveyor_id for selected orphaned persons
      const updatedPersons = allPersons.map((person) => {
        if (selectedGroups.has(person.surveyor_id || 'unknown')) {
          return {
            ...person,
            surveyor_id: currentUserId,
            updated_at: new Date().toISOString(),
            synced_at: '1970-01-01T00:00:00.000Z', // Mark as needing sync
          };
        }
        return person;
      });

      // Save updated data
      await storageService.setUserData({
        ...appData!,
        persons: updatedPersons,
        updated_at: new Date().toISOString(),
        pending_sync: true,
      });

      alert(t('orphanedRecords_importSuccess').replace('{count}', totalRecords.toString()));
      
      // Reload to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Error importing orphaned persons:', error);
      alert(t('orphanedRecords_importError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteOrphaned = async () => {
    const orphaned = orphanedGroups.filter((g) => g.isOrphaned);
    if (orphaned.length === 0) return;

    const totalRecords = orphaned.reduce((sum, g) => sum + g.count, 0);

    const confirmed = window.confirm(
      t('orphanedRecords_confirmDelete').replace('{count}', totalRecords.toString())
    );

    if (!confirmed) return;

    setProcessing(true);
    try {
      const orphanedSurveyorIds = new Set(orphaned.map((g) => g.surveyorId));
      const allPersons = appData?.persons || [];
      
      // Filter out orphaned persons
      const filteredPersons = allPersons.filter(
        (person) => !orphanedSurveyorIds.has(person.surveyor_id || 'unknown')
      );

      // Save updated data
      await storageService.setUserData({
        ...appData!,
        persons: filteredPersons,
        updated_at: new Date().toISOString(),
        pending_sync: true,
      });

      alert(t('orphanedRecords_deleteSuccess').replace('{count}', totalRecords.toString()));
      
      // Reload page
      loadOrphanedPersons();
    } catch (error) {
      console.error('Error deleting orphaned persons:', error);
      alert(t('orphanedRecords_deleteError'));
    } finally {
      setProcessing(false);
    }
  };

  const currentUserPersons = appData?.persons?.filter((p) => {
    const currentUserId = (currentUser as any)?.uuid || currentUser?.id;
    return p.surveyor_id === currentUserId;
  }).length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/settings')}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
              {t('back')}
            </button>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('orphanedRecords_title')}</h1>
          <p className="text-gray-600 text-sm">{t('orphanedRecords_description')}</p>
        </div>

        {/* Current User Stats */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orphanedRecords_currentUser')}</h2>
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-500">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
              {currentUser?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{currentUser?.name}</p>
              <p className="text-sm text-gray-600">{currentUser?.email}</p>
              <p className="text-xs text-gray-500">
                {t('orphanedRecords_recordCount').replace('{count}', currentUserPersons.toString())}
              </p>
            </div>
          </div>
        </div>

        {/* Deleted Person Records - Locally */}
        {deletedRecords.locallyDeleted.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('deletedRecords_localTitle')}</h2>
                <p className="text-sm text-gray-600 mt-1">{t('deletedRecords_localDescription')}</p>
              </div>
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium min-w-24 text-center">
                {convertNumberInCurrentLang(lang, deletedRecords.locallyDeleted.length)} {t('orphanedRecords_recordCount').replace('{count}', '')}
              </span>
            </div>
            <div className="space-y-2">
              {deletedRecords.locallyDeleted.map((record, index) => (
                <div
                  key={record.id || index}
                  className="flex items-center gap-4 p-3 rounded-lg bg-orange-50 border border-orange-200"
                >
                  <div className="w-10 h-10 bg-orange-400 rounded-full flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{record.name || record.id}</p>
                    <p className="text-sm text-gray-600">
                      {t('deletedRecords_deletedOn')} {convertInCurrentLang(lang, new Date(record.deleted_at).toLocaleDateString())}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-lg bg-orange-200 text-orange-800 font-medium">
                    {t('deletedRecords_pendingSync')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deleted Person Records - Server */}
        {deletedRecords.serverDeleted.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('deletedRecords_serverTitle')}</h2>
                <p className="text-sm text-gray-600 mt-1">{t('deletedRecords_serverDescription')}</p>
              </div>
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium min-w-24 text-center">
                {convertNumberInCurrentLang(lang, deletedRecords.serverDeleted.length)} {t('orphanedRecords_recordCount').replace('{count}', '')}
              </span>
            </div>
            <div className="space-y-2">
              {deletedRecords.serverDeleted.map((record, index) => (
                <div
                  key={record.id || index}
                  className="flex items-center gap-4 p-3 rounded-lg bg-red-50 border border-red-200"
                >
                  <div className="w-10 h-10 bg-red-400 rounded-full flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{record.name || record.id}</p>
                    <p className="text-sm text-gray-600">
                      {t('deletedRecords_deletedOn')} {"-"} { convertInCurrentLang(lang, new Date(record.deleted_at).toLocaleDateString())}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-lg bg-red-200 text-red-800 font-medium">
                    {t('deletedRecords_synced')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orphaned Person Records */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('orphanedRecords_groupsTitle')}</h2>
            <div className="flex gap-2">
              {selectedGroups.size > 0 && (
                <button
                  onClick={handleImportSelected}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {t('orphanedRecords_importSelected')} ({Array.from(selectedGroups).reduce((sum, id) => {
                    const group = orphanedGroups.find((g) => g.surveyorId === id);
                    return sum + (group?.count || 0);
                  }, 0)})
                </button>
              )}
              {orphanedGroups.some((g) => g.isOrphaned) && (
                <button
                  onClick={handleDeleteOrphaned}
                  disabled={processing}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {t('orphanedRecords_deleteOrphaned')}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">
              {t('orphanedRecords_checkingStatus')}
            </div>
          ) : orphanedGroups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('orphanedRecords_noOrphanedGroups')}
            </div>
          ) : (
            <div className="space-y-3">
              {orphanedGroups.map((group) => {
                const isSelected = selectedGroups.has(group.surveyorId);
                
                return (
                  <div
                    key={group.surveyorId}
                    className={`flex items-center gap-4 p-4 rounded-lg border-2 transition cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleGroupSelection(group.surveyorId)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold text-gray-900">Surveyor ID: {group.surveyorId}</p>
                        {group.isOrphaned === null ? (
                          <span className="px-2 py-0.5 text-sm rounded-lg bg-gray-200 text-gray-600">
                            {t('orphanedRecords_checkingStatus')}
                          </span>
                        ) : group.isOrphaned ? (
                          <span className="px-2 py-0.5 text-sm rounded-lg bg-red-100 text-red-700 font-medium">
                            {t('orphanedRecords_orphaned')}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-sm rounded-lg bg-green-100 text-green-700 font-medium">
                            {t('orphanedRecords_active')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-base text-gray-500 mt-1">
                        <span className="font-medium text-blue-600">
                          {t('orphanedRecords_recordCount').replace('{count}', group.count.toString())}
                        </span>
                      </div>
                      {/* Show first few person names */}
                      <div className="mt-2 text-sm text-gray-600">
                        {group.persons.slice(0, 3).map((p) => p.name).join(', ')}
                        {group.count > 3 && ` +${group.count - 3} more`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
